/**
 * Returns the value, or throws when it is nil.
 *
 * @param value
 * @param message the message of the thrown error.
 */
export function assertNotNil<T>(
  value: T | null | undefined,
  message: string,
): Exclude<T, null | undefined> {
  if (value === null || value === undefined) {
    throw new Error(message)
  }
  return value as Exclude<T, null | undefined>
}

/**
 * Attempts to extract the error message from the given error.
 *
 * @param err
 */
export function extractMessageFromError(err: unknown): string {
  if (err === undefined || err === null || err === false) {
    return ''
  }

  if (typeof err === 'string') {
    return err
  }

  const fieldErrors = getFieldErrors(err)
  if (fieldErrors.length > 0) {
    return (
      'Validation errors: ' +
      fieldErrors.map((v) => `${v.field} ${v.message}`).join(', ')
    )
  }

  if (err instanceof Error) {
    return err.message + ''
  }

  return 'An error occurred, but not sure what it is.'
}

/**
 * A validation error for a single field.
 */
export interface FieldError {
  field: string
  message: string
}

/**
 * Gets the field errors of a validation error response, which
 * `ApiClient.throwIfNotSuccessful` copies onto the HTTP error.
 */
export function getFieldErrors(err: unknown): FieldError[] {
  const errors = (err as { errors?: unknown } | null | undefined)?.errors
  return Array.isArray(errors) ? (errors as FieldError[]) : []
}
